import {memo, useMemo} from "react";
import {Box, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import CakeOutlined from "@mui/icons-material/CakeOutlined";
import HotelIcon from "@mui/icons-material/Hotel";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import LoginOutlined from "@mui/icons-material/LoginOutlined";
import {
    FloorplanListHalfboardDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanListHalfboard.dto";
import {
    Reservation,
    ReservationStatus
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    formatDateHM,
    formatDateRangeShortDayMonth
} from "crqlar-react-common/dist/utils/date.util";
import {
    getIconForLanguage,
    getNameForLanguage
} from "crqlar-react-common/dist/utils/translations.util";
import {getReservationDisplayGuestCounts} from "crqlar-react-common/dist/utils/reservationGuestCounts.util";
import {filterVisibleNotes} from "crqlar-react-common/dist/utils/noteVisibility.util";
import {useNoteScope} from "crqlar-react-common/dist/utils/hooks/notes/useNoteScope";
import {useTimeframe} from "../hooks/useTimeframe";
import {ALLERGY_ICON_MAP, getHalfboardRateLabel, getReservationDisplay} from "../utils/reservation-utils";
import {useTranslation} from "react-i18next";
import {AgeCategoriesElement} from "../../../../common/uiElements/AgeCategoriesElement";
import {NotesTooltip} from "../../../../common/uiElements/NotesElement";

interface Props {
    halfboard: FloorplanListHalfboardDTO;
    onClickEdit?: (reservation: Reservation) => void;
    isDragOverlay?: boolean;
    draggable?: boolean;
}

const ReadSidebarHalfboardItem: React.FC<Props> = ({
                                                       halfboard,
                                                       onClickEdit,
                                                       isDragOverlay,
                                                       draggable = true,
                                               }) => {
    const {t} = useTranslation();
    const {referenceTime} = useTimeframe();
    const {
        guestDisplayName,
        primaryLanguage,
        start,
        end,
        roomNames,
        roomNamesInline,
        fromBookingEngine,
        birthdayNames,
        isBirthday,
        preferences,
    } = getReservationDisplay({
        reservation: halfboard.accommodationReservation,
        profiles: halfboard.profiles,
        ageCategories: halfboard.ageCategories,
        accommodationResources: halfboard.accommodationResources,
    });
    const isPastItem = !isDragOverlay && !!end && end < referenceTime;
    const status = halfboard.accommodationReservation?.status;
    const isCheckedIn = status === ReservationStatus.ACTIVE
        || status === ReservationStatus.COMPLETED
        || (!!start && start < referenceTime);
    const displayName = guestDisplayName || "—";

    const stayRange = useMemo(() => formatDateRangeShortDayMonth(start, end), [start, end]);
    const rateLabel = getHalfboardRateLabel(halfboard);

    // The API already strips notes outside the signed-in user's note-type scope, but this list is
    // merged client-side from the profiles and the accommodation reservation, so the same scope is
    // re-applied to the merge.
    const noteScope = useNoteScope();
    const notes = useMemo(() => {
        const profileNotes = halfboard.profiles?.flatMap(p => p.internalNotes ?? []) ?? [];
        const reservationNotes = halfboard.accommodationReservation?.notes ?? [];

        // Passed through as note documents, not plain text, so the popup can badge each one with
        // its department.
        return filterVisibleNotes([...profileNotes, ...reservationNotes], noteScope)
            .filter(note => !note.hidden && !!note.note?.trim());
    }, [halfboard.profiles, halfboard.accommodationReservation, noteScope]);

    const card = (
        <Box
            sx={{
                display: "flex",
                alignItems: "stretch",
                opacity: isPastItem ? 0.6 : 1,
                ...(isDragOverlay
                    ? {
                        bgcolor: "#fff",
                        borderRadius: 2,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    }
                    : {
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:hover": {bgcolor: "action.hover"},
                        "&:hover .edit-reveal": {
                            maxWidth: 32,
                            opacity: 1,
                        },
                        "&:focus-within .edit-reveal": {
                            maxWidth: 32,
                            opacity: 1,
                        },
                        "@media (hover: none)": {
                            "& .edit-reveal": {
                                maxWidth: 32,
                                opacity: 1,
                            },
                        },
                    }),
            }}
        >
            <Box sx={{pl: "16px", display: "flex", alignItems: "center"}}>
                <Box
                    sx={{
                        width: 10,
                        flex: "0 0 10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "text.secondary",
                    }}
                >
                    {draggable && <DragIndicatorIcon fontSize="small"/>}
                </Box>
            </Box>

            <Box sx={{flex: 1, minWidth: 0, pl: 2, pr: 1.5, py: 1.5}}>
                {/* Row 1: Flag + Guest name + Icons + Edit */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: 0
                }}>
                    {primaryLanguage && (
                        <Box component="img"
                             alt={getNameForLanguage(primaryLanguage)}
                             src={getIconForLanguage(primaryLanguage)}
                             sx={{
                                 width: 14,
                                 height: 14,
                                 borderRadius: '50%',
                                 objectFit: 'cover',
                                 border: '0.5px solid rgba(0,0,0,0.15)',
                                 flex: "0 0 auto",
                                 marginTop: "-4px"
                             }}
                        />
                    )}
                    <Typography noWrap
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "0.9375rem",
                                    minWidth: 0,
                                    flex: "1 1 0"
                                }}>
                        {displayName}
                    </Typography>
                    {!isCheckedIn && (
                        <Tooltip title={`${displayName} ${t("checks in at")} ${start ? formatDateHM(start) : '—'}`} arrow>
                            <LoginOutlined sx={{fontSize: 14, color: "info.main", cursor: "default"}}/>
                        </Tooltip>
                    )}
                    {isBirthday && (
                        <Tooltip title={birthdayNames.map(name => t("It's {{name}}'s birthday", {name})).join(", ")} arrow>
                            <CakeOutlined sx={{fontSize: 14, color: "#9c27b0", cursor: "default"}}/>
                        </Tooltip>
                    )}
                    {preferences.length > 0 && (
                        <Tooltip arrow title={
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {preferences.map((pref, i) => {
                                    const mapping = ALLERGY_ICON_MAP[pref.preferenceType!];
                                    if (!mapping) return null;
                                    const Icon = mapping.icon;
                                    return (
                                        <Stack key={`${pref.preferenceValue}-${i}`} direction="row" alignItems="center" gap={0.25}>
                                            <Icon sx={{fontSize: 12, color: mapping.color}}/>
                                            <Typography fontSize="0.7rem">{pref.preferenceValue}</Typography>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        }>
                            <Stack direction="row" gap={0.25} sx={{cursor: "default"}}>
                                {Array.from(new Set(preferences.map(p => p.preferenceType))).map(type => {
                                    const mapping = ALLERGY_ICON_MAP[type!];
                                    if (!mapping) return null;
                                    const Icon = mapping.icon;
                                    return <Icon key={type} sx={{fontSize: 14, color: mapping.color}}/>;
                                })}
                            </Stack>
                        </Tooltip>
                    )}
                    <Box
                        className="edit-reveal"
                        sx={{
                            maxWidth: 0,
                            opacity: 0,
                            overflow: "hidden",
                            transition: "max-width 0.2s cubic-bezier(.4,0,.2,1), opacity 0.15s ease",
                        }}
                    >
                        <Tooltip title={t("Edit reservation")} arrow>
                            <IconButton
                                size="small"
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() => onClickEdit?.(halfboard.accommodationReservation)}
                                sx={{p: 0.5}}
                            >
                                <EditIcon sx={{fontSize: 16}}/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Row 2: Room · Age categories · Stay range */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mt: 0.5,
                    minWidth: 0
                }}>
                    {fromBookingEngine && (<>
                        <Tooltip title={t("Online Reservation")} arrow>
                            <BookOnlineIcon sx={{
                                fontSize: 14,
                                color: "text.secondary",
                                flex: "0 0 auto",
                                cursor: "default"
                            }}/>
                        </Tooltip>
                        <Typography color="text.disabled" fontSize="0.75rem"
                                    sx={{flex: "0 0 auto"}}>·</Typography>
                    </>)}

                    {roomNames && (<>
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            minWidth: 0,
                            flex: "0 0 auto"
                        }}>
                            <HotelIcon sx={{
                                fontSize: 14,
                                color: "text.secondary",
                                flex: "0 0 auto"
                            }}/>
                            <Typography noWrap color="text.secondary"
                                        fontSize="0.75rem" title={roomNames}>
                                {roomNamesInline}
                            </Typography>
                        </Box>
                        <Typography color="text.disabled" fontSize="0.75rem"
                                    sx={{flex: "0 0 auto"}}>·</Typography>
                    </>)}

                    <AgeCategoriesElement
                        ageCategories={halfboard.ageCategories}
                        ageCategoryCounts={getReservationDisplayGuestCounts(halfboard.accommodationReservation)}
                        variant="inline"
                    />

                    <Typography color="text.disabled" fontSize="0.75rem"
                                sx={{flex: "0 0 auto"}}>·</Typography>

                    <Typography color="text.secondary" fontSize="0.75rem"
                                noWrap title={stayRange}
                                sx={{flex: "0 0 auto"}}>
                        {stayRange}
                    </Typography>

                    {rateLabel && (<>
                        <Typography color="text.disabled" fontSize="0.75rem"
                                    sx={{flex: "0 0 auto"}}>·</Typography>
                        <Typography noWrap color="text.secondary" fontSize="0.75rem"
                                    sx={{flex: "1 1 auto", minWidth: 0}}
                                    title={rateLabel}>
                            {rateLabel}
                        </Typography>
                    </>)}
                </Box>
            </Box>
        </Box>
    );

    // The drag overlay follows the cursor and must never sprout a popup of its own.
    if (isDragOverlay) return card;

    return (
        <NotesTooltip notes={notes} placement="left-start">
            {card}
        </NotesTooltip>
    );
};

export default memo(ReadSidebarHalfboardItem);
